import React, { useState } from 'react';
import { Card, Rate, Tag, Button, Avatar, Typography, Image, Badge, Space, Tooltip } from 'antd';
import { 
  StarOutlined, 
  ClockCircleOutlined, 
  PhoneOutlined, 
  EnvironmentOutlined,
  HeartOutlined,
  HeartFilled,
  EyeOutlined,
  ShopOutlined
} from '@ant-design/icons';
import { getImageForCard, getImageWithFallback, handleImageError } from '../../utils/images';

const { Text, Title } = Typography;

const MarketplaceVendorCard = ({ 
  vendor, 
  onViewDetails, 
  onToggleFavorite,
  isFavorite = false,
  showFavorite = true,
  showQuickView = true,
  cardStyle = {},
  imageHeight = 200,
  showStats = true
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleViewDetails = () => {
    if (onViewDetails) {
      onViewDetails(vendor);
    }
  };

  const handleToggleFavorite = (e) => {
    e.stopPropagation();
    if (onToggleFavorite) {
      onToggleFavorite(vendor);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'green';
      case 'closed': return 'red';
      case 'busy': return 'orange';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'open': return 'Open Now';
      case 'closed': return 'Closed';
      case 'busy': return 'Busy';
      default: return 'Unknown';
    }
  };

  const getCuisineTags = () => {
    if (vendor.cuisines && Array.isArray(vendor.cuisines)) {
      return vendor.cuisines.slice(0, 3);
    }
    return [];
  };

  const getDeliveryTime = () => {
    if (vendor.preparation_time) {
      return `${vendor.preparation_time} min`;
    }
    return 'N/A';
  };

  const getMinOrderAmount = () => {
    if (vendor.min_order_amount) {
      return `₹${vendor.min_order_amount} min order`;
    }
    return 'No minimum';
  };

  const cuisineTags = getCuisineTags();

  return (
    <Card
      hoverable
      className="marketplace-vendor-card"
      style={{
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid #f0f0f0',
        transition: 'all 0.3s ease',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: isHovered ? '0 8px 25px rgba(0,0,0,0.15)' : '0 2px 8px rgba(0,0,0,0.1)',
        ...cardStyle
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      bodyStyle={{ padding: 0 }}
      onClick={handleViewDetails}
    >
      {/* Cover Image Section */}
      <div 
        className="relative"
        style={{ height: `${imageHeight}px`, overflow: 'hidden' }}
      >
        <Image
          src={getImageWithFallback(vendor, 'medium')}
          alt={vendor.business_name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
          preview={false}
          fallback="/images/placeholder.svg"
          onError={handleImageError}
        />
        
        {/* Overlay with actions */}
        <div 
          className="absolute inset-0 bg-black bg-opacity-0 transition-all duration-300 flex items-center justify-center"
          style={{
            backgroundColor: isHovered ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0)'
          }}
        >
          <Space size="middle" style={{ opacity: isHovered ? 1 : 0, transition: 'opacity 0.3s ease' }}>
            {showQuickView && (
              <Tooltip title="View Details">
                <Button
                  type="primary"
                  shape="circle"
                  icon={<EyeOutlined />}
                  style={{ 
                    background: 'rgba(255,255,255,0.9)',
                    border: 'none',
                    color: '#1890ff'
                  }}
                />
              </Tooltip>
            )}
          </Space>
        </div>

        {/* Favorite Button */}
        {showFavorite && (
          <Button
            type="text"
            shape="circle"
            icon={isFavorite ? <HeartFilled /> : <HeartOutlined />}
            onClick={handleToggleFavorite}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: 'rgba(255,255,255,0.9)',
              border: 'none',
              color: isFavorite ? '#ff4d4f' : '#8c8c8c',
              zIndex: 10
            }}
          />
        )}

        {/* Status Badge */}
        <div style={{ position: 'absolute', top: '8px', left: '8px' }}>
          <Tag color={getStatusColor(vendor.status || 'open')} style={{ margin: 0 }}>
            {getStatusText(vendor.status || 'open')}
          </Tag>
        </div>

        {/* Verification Badge */}
        {vendor.is_verified && (
          <div style={{ position: 'absolute', top: '8px', right: '48px' }}>
            <Badge 
              count="✓" 
              style={{ 
                backgroundColor: '#52c41a',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold'
              }}
            />
          </div>
        )}

        {/* Logo */}
        <div style={{ 
          position: 'absolute', 
          bottom: '-30px', 
          left: '16px',
          zIndex: 5
        }}>
          <Avatar
            size={60}
            src={vendor.logo_image_path ? getImageWithFallback({ image: vendor.logo_image_path }, 'medium') : null}
            icon={<ShopOutlined />}
            style={{
              border: '3px solid white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}
            onError={handleImageError}
          />
        </div>
      </div>

      {/* Content Section */}
      <div style={{ padding: '24px 16px 16px 16px', marginTop: '30px' }}>
        {/* Vendor Name and Rating */}
        <div style={{ marginBottom: '8px' }}>
          <Title level={4} style={{ margin: 0, fontSize: '18px' }}>
            {vendor.business_name}
          </Title>
          {vendor.rating && (
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '4px' }}>
              <Rate 
                disabled 
                value={vendor.rating} 
                style={{ fontSize: '14px' }}
              />
              <Text type="secondary" style={{ fontSize: '12px', marginLeft: '4px' }}>
                ({vendor.total_reviews || 0} reviews)
              </Text>
            </div>
          )}
        </div>

        {/* Description */}
        {vendor.description && (
          <Text 
            type="secondary" 
            style={{ 
              fontSize: '13px', 
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: '1.4'
            }}
          >
            {vendor.description}
          </Text>
        )}

        {/* Cuisine Tags */}
        {cuisineTags.length > 0 && (
          <div style={{ marginTop: '8px', marginBottom: '8px' }}>
            <Space size={4} wrap>
              {cuisineTags.map((cuisine, index) => (
                <Tag key={index} color="blue" style={{ fontSize: '11px' }}>
                  {cuisine}
                </Tag>
              ))}
            </Space>
          </div>
        )}

        {/* Stats */}
        {showStats && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            marginTop: '12px',
            padding: '8px 0',
            borderTop: '1px solid #f0f0f0'
          }}>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <ClockCircleOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
              <div style={{ fontSize: '11px', color: '#8c8c8c', marginTop: '2px' }}>
                {getDeliveryTime()}
              </div>
            </div>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <Text style={{ fontSize: '11px', color: '#8c8c8c' }}>
                {getMinOrderAmount()}
              </Text>
            </div>
            {vendor.phone && (
              <div style={{ textAlign: 'center', flex: 1 }}>
                <PhoneOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
                <div style={{ fontSize: '11px', color: '#8c8c8c', marginTop: '2px' }}>
                  Call
                </div>
              </div>
            )}
          </div>
        )}

        {/* Location */}
        {vendor.address && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            marginTop: '8px',
            padding: '4px 0'
          }}>
            <EnvironmentOutlined style={{ color: '#8c8c8c', fontSize: '12px', marginRight: '4px' }} />
            <Text type="secondary" style={{ fontSize: '11px' }}>
              {vendor.address.length > 50 ? `${vendor.address.substring(0, 50)}...` : vendor.address}
            </Text>
          </div>
        )}

        {/* Action Button */}
        <div style={{ marginTop: '12px' }}>
          <Button
            type="primary"
            block
            style={{ borderRadius: '6px' }}
            onClick={handleViewDetails}
          >
            View Menu
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default MarketplaceVendorCard;
